import React from "react";

import { ROUTE_CALENDAR } from "../../../config/lang";
import { translate } from "../../../util/util";
import { isSameMonth, isToday, monthGrid, monthLabel, nthOfMonth, weekdayHeaders } from "./dateUtils";
import { isDateKey, isWeekKey, mergedStopsForDate } from "./routeModel";
import { POINT_DND_TYPE } from "./PointSearchPanel";

const ORDINAL_KEY = { 1: "ord_first", 2: "ord_second", 3: "ord_third", 4: "ord_fourth", 5: "ord_fifth" };

// Short label for a stop's layer, shown as a small badge (weekly / Nth / one-off).
const layerBadge = (stop, date, lang) => {
  if (isDateKey(stop.layerKey)) return { text: translate(ROUTE_CALENDAR, "badge_date", lang), cls: "rc-badge-date" };
  if (isWeekKey(stop.layerKey)) return { text: translate(ROUTE_CALENDAR, ORDINAL_KEY[nthOfMonth(date)], lang), cls: "rc-badge-nth" };
  return { text: translate(ROUTE_CALENDAR, "badge_weekly", lang), cls: "rc-badge-weekly" };
};

function DayCell({ date, monthDate, layers, lang, onDrop, onRemove, onMove }) {
  const [over, setOver] = React.useState(false);
  const inMonth = isSameMonth(date, monthDate);
  const stops = inMonth ? mergedStopsForDate(date, layers) : [];

  const handleDrop = e => {
    e.preventDefault();
    setOver(false);
    if (!inMonth) return;
    const raw = e.dataTransfer.getData(POINT_DND_TYPE);
    if (!raw) return;
    try {
      onDrop(date, JSON.parse(raw));
    } catch (_) {
      /* ignore malformed payloads */
    }
  };

  return (
    <div
      className={
        "rc-cell" +
        (inMonth ? "" : " rc-cell-muted") +
        (isToday(date) ? " rc-cell-today" : "") +
        (over ? " rc-cell-over" : "")
      }
      onDragOver={e => {
        if (!inMonth) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = "copy";
        if (!over) setOver(true);
      }}
      onDragLeave={() => over && setOver(false)}
      onDrop={handleDrop}
    >
      <div className="rc-cell-head">
        <span className="rc-cell-date">{date.getDate()}</span>
      </div>
      <div className="rc-cell-stops">
        {stops.map((stop, idx) => {
          const badge = layerBadge(stop, date, lang);
          const canUp = idx > 0 && stops[idx - 1].layerKey === stop.layerKey;
          const canDown = idx < stops.length - 1 && stops[idx + 1].layerKey === stop.layerKey;
          return (
            <div className={"rc-stop " + badge.cls} key={stop.layerKey + "_" + stop.point.id} title={stop.point.address || stop.point.name}>
              <span className="rc-stop-badge">{badge.text}</span>
              <span className="rc-stop-name">{stop.point.name}</span>
              <span className="rc-stop-actions">
                <button type="button" className="rc-stop-btn" disabled={!canUp} onClick={() => onMove(stop.layerKey, stop.point.id, -1)} aria-label="move up">
                  <span className="material-symbols-outlined">arrow_upward</span>
                </button>
                <button type="button" className="rc-stop-btn" disabled={!canDown} onClick={() => onMove(stop.layerKey, stop.point.id, 1)} aria-label="move down">
                  <span className="material-symbols-outlined">arrow_downward</span>
                </button>
                <button type="button" className="rc-stop-btn rc-stop-remove" onClick={() => onRemove(stop.layerKey, stop.point.id)} aria-label="remove">
                  <span className="material-symbols-outlined">close</span>
                </button>
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function RouteCalendar({ monthDate, layers, lang, onPrev, onNext, onDrop, onRemove, onMove }) {
  const weeks = monthGrid(monthDate);
  const headers = weekdayHeaders(lang);

  return (
    <div className="rc-calendar">
      <div className="rc-cal-header">
        <button type="button" className="rc-nav-btn" onClick={onPrev} aria-label="previous month">
          <span className="material-symbols-outlined">chevron_left</span>
        </button>
        <span className="rc-month-label">{monthLabel(monthDate, lang)}</span>
        <button type="button" className="rc-nav-btn" onClick={onNext} aria-label="next month">
          <span className="material-symbols-outlined">chevron_right</span>
        </button>
      </div>

      <div className="rc-grid rc-grid-headers">
        {headers.map((h, i) => (
          <div className="rc-weekday" key={i}>
            {h}
          </div>
        ))}
      </div>

      <div className="rc-grid rc-grid-days">
        {weeks.map((row, wi) =>
          row.map((date, di) => (
            <DayCell
              key={wi + "_" + di}
              date={date}
              monthDate={monthDate}
              layers={layers}
              lang={lang}
              onDrop={onDrop}
              onRemove={onRemove}
              onMove={onMove}
            />
          ))
        )}
      </div>
    </div>
  );
}
